import { JSXElementConstructor, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import type { GlyphProps, CustomGlyphProps } from '@atlaskit/icon/types';
import Lozenge from '@atlaskit/lozenge';
import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import {
	getJsmPlaygroundSignupIntent,
	JSM_PLAYGROUND_SIGNUP_SOURCE,
} from '@/components/signup/utils/jsm-playground-signup-intent';
import type { JsmPlaygroundSignupIntent } from '@/components/signup/utils/jsm-playground-signup-intent';
import { ProductKey } from '@/components/signup/utils/products';
import { ufoEventConfirmationPageTeamTypeSelection } from '@/components/signup/utils/ufo-events';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { wacToken } from '@/libraries/design/foundations';
import { useConfirmationPage } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-confirmation-page.tsx';
import { useScreenManager } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-screen-manager.tsx';
import { updateUserProfile } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/services/update-user-profile';
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

import { useGupsStatus } from '../../hooks/use-gups';
import { useUserSegmentationEvents } from '../../hooks/use-user-segmentation-events';
import { useWorkStyleChoiceTreatment } from '../../hooks/use-work-style-choice-treatment';
import {
	getIdentityTeamType,
	UserSegmentationTeamTypes,
	USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
} from '../../models/user-segmentation';
import { getCurrentUrlWithParams } from '../../utils/utils';
import { getJsmRecommendedTemplateId } from '../jsm-use-case-template-choice/jsm-use-case-template-choice-constants';

type UserSegCardProps = {
	readonly card: string;
	readonly Icon: JSXElementConstructor<GlyphProps> | JSXElementConstructor<CustomGlyphProps>;
	readonly teamType: UserSegmentationTeamTypes;
	readonly idx: number;
	readonly onClick?: () => void;
	readonly afterClick?: () => void;
	readonly disabled?: boolean;
	readonly shouldFitContainer?: boolean;
	readonly hasScrollbarBugfix?: boolean;
	/**
	 * JSMGROWTH-535: the team type carried over from the JSM playground page.
	 * Shown with a "Recommended" lozenge instead of a selected state so the
	 * user still makes an explicit choice.
	 */
	readonly isRecommended?: boolean;
	readonly isCardRefreshEnabled?: boolean;
};

const getSelectionUrlParameters = ({
	playgroundSignupIntent,
	recommendedTemplateId,
	shouldRouteCustomerServiceToJcs,
	teamType,
}: {
	readonly playgroundSignupIntent: JsmPlaygroundSignupIntent | undefined;
	readonly recommendedTemplateId: string | undefined;
	readonly shouldRouteCustomerServiceToJcs: boolean;
	readonly teamType: UserSegmentationTeamTypes;
}): Record<string, string> => {
	if (playgroundSignupIntent && recommendedTemplateId) {
		return {
			signupSource: JSM_PLAYGROUND_SIGNUP_SOURCE,
			team_type: teamType,
			templateId: recommendedTemplateId,
		};
	}

	return {
		team_type: teamType,
		...(shouldRouteCustomerServiceToJcs
			? { primaryCollectionProduct: ProductKey.JIRA_CUSTOMER_SERVICE }
			: {}),
	};
};

const shouldNavigateBeforeProfileUpdate = ({
	isCustomerServiceSelected,
	isJsmUseCaseExperimentEnabled,
	isServiceCollectionFlow,
	isWorkStyleChoiceEnabled,
	playgroundSignupIntent,
}: {
	readonly isCustomerServiceSelected: boolean;
	readonly isJsmUseCaseExperimentEnabled: boolean;
	readonly isServiceCollectionFlow: boolean;
	readonly isWorkStyleChoiceEnabled: boolean;
	readonly playgroundSignupIntent: JsmPlaygroundSignupIntent | undefined;
}) =>
	Boolean(playgroundSignupIntent) ||
	isWorkStyleChoiceEnabled ||
	(isServiceCollectionFlow && isJsmUseCaseExperimentEnabled && !isCustomerServiceSelected);

export const UserSegCard = ({
	card,
	Icon,
	teamType,
	onClick,
	afterClick,
	disabled,
	shouldFitContainer,
	hasScrollbarBugfix,
	isRecommended = false,
	isCardRefreshEnabled = false,
}: UserSegCardProps) => {
	const intl = useIntl();
	const {
		userSegmentationContext: {
			closeUserSegmentationUI,
			setSelectedTeamType,
			setSelectedTemplateId,
		},
	} = useConfirmationPage();

	const { addTeamTypeCallback } = useGupsStatus();
	const { products } = useSearchParameters();
	const playgroundSignupIntent = useMemo(
		() => getJsmPlaygroundSignupIntent(window.location.search),
		[],
	);

	const { nextScreen } = useScreenManager();

	const stargateClient = useBxpWebStargateClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isHovering, setIsHovering] = useState(false);
	const { fireTeamTypeProcessedEvent } = useUserSegmentationEvents();
	const jsmGrowth519Result = useExperiment(experiments.jsmGrowth519JsmLandUseCaseOnboarding);
	const isJsmUseCaseExperimentEnabled =
		jsmGrowth519Result.type === 'enrolled'
			? jsmGrowth519Result.value
			: experiments.jsmGrowth519JsmLandUseCaseOnboarding.defaultValue;
	const { isEnabled: isWorkStyleChoiceEnabled } = useWorkStyleChoiceTreatment();

	const onClickHandler = async () => {
		const isTeamworkCollectionFlow = products.includes(ProductKey.TEAMWORK_COLLECTION);
		const isServiceCollectionFlow = products.includes(ProductKey.SERVICE_COLLECTION);

		try {
			ufoEventConfirmationPageTeamTypeSelection.start();
			ufoEventConfirmationPageTeamTypeSelection.setCustomData({
				teamType: teamType,
			});
			if (teamType === undefined) {
				throw new Error('teamType is undefined');
			}

			const recommendedTemplateId = playgroundSignupIntent
				? getJsmRecommendedTemplateId(teamType)
				: undefined;
			if (playgroundSignupIntent && !recommendedTemplateId) {
				throw new Error(`No recommended JSM template found for team type: ${teamType}`);
			}

			onClick?.();

			if (!isTeamworkCollectionFlow && !isServiceCollectionFlow) {
				addTeamTypeCallback(teamType);
			}

			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: TRACKING_EVENT_ACTION_SUBJECT_IDS.teamFocusTeamTypeButton,
				attributes: {
					atlasTicket: 'ATLAS-69623',
					teamType: teamType,
					...((isTeamworkCollectionFlow || isServiceCollectionFlow) && {
						productKey: products[0],
					}),
				},
			}).fire();

			// map the selected team type to the value
			// downstream consumers are expecting.
			const teamTypeFromIdentity = getIdentityTeamType(teamType);

			const isCustomerServiceSelected = teamType === USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE;
			const shouldRouteCustomerServiceToJcs =
				!playgroundSignupIntent && isServiceCollectionFlow && isCustomerServiceSelected;
			const shouldOptimisticallyNavigate = shouldNavigateBeforeProfileUpdate({
				isCustomerServiceSelected,
				isJsmUseCaseExperimentEnabled,
				isServiceCollectionFlow,
				isWorkStyleChoiceEnabled,
				playgroundSignupIntent,
			});

			const completeSelection = () => {
				setSelectedTeamType(teamType);
				if (recommendedTemplateId) {
					setSelectedTemplateId(recommendedTemplateId);
				}
				fireTeamTypeProcessedEvent(teamType);

				// add team_type and primaryCollectionProduct to query param to maintain selection on refresh
				const updatedUrl = getCurrentUrlWithParams(
					getSelectionUrlParameters({
						playgroundSignupIntent,
						recommendedTemplateId,
						shouldRouteCustomerServiceToJcs,
						teamType,
					}),
				);
				history.replaceState({}, '', updatedUrl);

				closeUserSegmentationUI(); // TODO: remove closeUserSegmentationUI once confirmation page rearch is productionized.
				nextScreen();
				if (playgroundSignupIntent) {
					createAnalyticsEvent({
						eventType: 'ui',
						action: 'confirmed',
						actionSubject: 'jsmPlaygroundTeamType',
						attributes: {
							experiment: 'JSMGROWTH-535',
							initialTeamType: playgroundSignupIntent.teamType,
							selectedTeamType: teamType,
							selectionChanged: playgroundSignupIntent.teamType !== teamType,
							templateId: recommendedTemplateId,
						},
					}).fire();
				}
				ufoEventConfirmationPageTeamTypeSelection.success();
			};

			if (shouldOptimisticallyNavigate) {
				completeSelection();
				void Promise.resolve(
					updateUserProfile({
						selectedTeamType: teamTypeFromIdentity,
						stargateClient,
					}),
				).catch(() => undefined);
				return;
			}

			await updateUserProfile({
				selectedTeamType: teamTypeFromIdentity,
				stargateClient,
			});
			completeSelection();
		} catch (e) {
			ufoEventConfirmationPageTeamTypeSelection.failure({
				error: e,
			});
		} finally {
			afterClick?.();
		}
	};

	return (
		<button
			data-testid="team-type-button"
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			onFocus={() => setIsHovering(true)}
			onBlur={() => setIsHovering(false)}
			css={[
				buttonStyle,
				isCardRefreshEnabled && refreshedButtonStyle,
				shouldFitContainer && buttonFitContainerStyle,
				hasScrollbarBugfix && buttonScrollbarFixStyle,
			]}
			onClick={onClickHandler}
			disabled={disabled}
		>
			<div css={iconWrapper}>
				<Icon label="" primaryColor={'#FFF'} size="large" />
			</div>
			<span css={cardHeaderText}>{card}</span>
			{(isRecommended || isCardRefreshEnabled || isHovering) && (
				<div css={trailingContentWrapper}>
					{isRecommended && !isHovering && (
						<Lozenge testId="recommended-lozenge" isBold={false}>
							{intl.formatMessage({
								id: 'UserSeg.teamTypeSelection.jsmUseCase.recommended',
								defaultMessage: 'Recommended',
							})}
						</Lozenge>
					)}
					{isCardRefreshEnabled ? (
						<div
							css={[refreshedArrowIconWrapper, isHovering && arrowIconVisible]}
							data-testid="arrow-icon"
						>
							<ArrowRightIcon label="" />
						</div>
					) : (
						isHovering && (
							<div css={arrowIconWrapper} data-testid="arrow-icon">
								<ArrowRightIcon label="" />
							</div>
						)
					)}
				</div>
			)}
		</button>
	);
};

const buttonStyle = css({
	cursor: 'pointer',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-start',
	padding: '16px',
	gap: token('space.100'),
	height: '64px',
	borderRadius: '6px',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${token('color.background.accent.gray.subtler')}`,
	margin: '1px',
	boxSizing: 'border-box',
	position: 'relative',
	width: '100%',
	maxWidth: '415px',
	minWidth: 0,
	[':hover']: {
		borderColor: token('color.border.brand'),
		boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
	},
	[':focus-visible']: {
		borderColor: token('color.border.brand'),
		boxShadow: `0 0 0 1px ${token('color.border.brand')}`,
	},
	[media.above.sm]: {
		maxWidth: 'none',
	},
});

const refreshedButtonStyle = css({
	marginBlockStart: token('space.0', '0px'),
	marginInlineEnd: token('space.0', '0px'),
	marginBlockEnd: token('space.0', '0px'),
	marginInlineStart: token('space.0', '0px'),
	borderRadius: token('radius.large', '8px'),
	maxWidth: '440px',
	border: `1px solid ${token('color.border', '#0b120e24')}`,
	transitionProperty: 'border-color, box-shadow',
	transitionDuration: token('motion.duration.medium', '200ms'),
	transitionTimingFunction: token('motion.easing.inout.bold', 'cubic-bezier(0.4, 0, 0, 1)'),
	[':hover']: {
		borderColor: token('color.border.selected', '#1868DB'),
		boxShadow: `inset 0 0 0 1px ${token('color.border.selected', '#1868DB')}`,
	},
	[':focus-visible']: {
		borderColor: token('color.border.selected', '#1868DB'),
		boxShadow: `inset 0 0 0 1px ${token('color.border.selected', '#1868DB')}`,
	},
	[media.above.sm]: {
		maxWidth: 'none',
	},
});

// SPICE-2698: as a stretched grid item, `auto` fills the track minus the 1px
// margin. `100%` resolves to the full track width, so the margin pushes the card
// past its grid area and WebKit treats that as horizontal overflow.
const buttonScrollbarFixStyle = css({
	[media.above.sm]: {
		width: 'auto',
	},
});

const buttonFitContainerStyle = css({
	maxWidth: 'none',
});

const iconWrapper = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '40px',
	width: '40px',
	backgroundColor: 'none',
	flexShrink: 0,
});

// Keeps the "Recommended" lozenge and the hover arrow together at the end of
// the card; a single `auto` margin here avoids flex splitting the free space
// between two trailing items.
const trailingContentWrapper = css({
	marginInlineStart: 'auto',
	display: 'flex',
	alignItems: 'center',
	columnGap: token('space.100'),
	flexShrink: 0,
});

const arrowIconWrapper = css({
	marginBlockStart: 0,
	display: 'flex',
	alignItems: 'center',
});

const refreshedArrowIconWrapper = css({
	marginInlineStart: 'auto',
	display: 'flex',
	alignItems: 'center',
	opacity: 0,
	transform: `translateX(calc(-1 * ${token('space.100', '8px')}))`,
	transitionProperty: 'opacity, transform',
	transitionDuration: token('motion.duration.medium', '200ms'),
	transitionTimingFunction: token('motion.easing.inout.bold', 'cubic-bezier(0.4, 0, 0, 1)'),
});

const arrowIconVisible = css({
	opacity: 1,
	transform: 'translateX(0)',
});

const cardHeaderText = css({
	color: `${token('color.text')}`,
	fontFamily: 'var(--atlassian-sans-font-family)',
	fontSize: '16px',
	fontWeight: '700',
	lineHeight: '28px',
	textAlign: 'left',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	minWidth: 0,
	[media.above.sm]: {
		fontSize: 'clamp(12px, 1.5vw, 18px)',
		lineHeight: '1.4',
	},
});
